import { serialize } from 'serializr';

import { apiFetch, ApiRequest, Constructor, PagedList } from 'framework/api';
import { MonitorsGridEntry } from 'areas/assets/monitors/monitors-list/monitorsGridEntry';
import { MonitorType } from 'areas/assets/monitors/monitorType';
import { RuleDefinition, RulesConfiguration } from 'controls/queryBuilder/ruleDefinition';
import { GridStore } from 'controls/grid/gridStore';
import { ApiDocumentationSection } from 'controls/react/apiDocumentation';
import { MonitorBase } from 'areas/assets/monitors/shared-components/monitorBase';
import { ApplicationState } from 'framework/applicationState';
import { CeeViewDataSource } from 'tools/ceeViewDataSource';
import { ErrorHandler } from '../../../core';
import Settings from '../../../settings';
import { AssetDropDown } from '../../../controls/react/dropdowns/assetDropDown';
import { GridDataItem } from 'controls/grid/gridDataItem';
import { copyViaSerializr } from 'framework/serializr-integration';
import {
	applyToMonitorCachedData,
	getMonitorUpdatesFromCache,
} from 'areas/monitors/components/add-asset-group-button/addAssetGroupButton';
import { ServiceableMonitor } from 'areas/assets/monitors/maintenanceView';

export type GetMonitorsPayload = {
	monitorType?: MonitorType;
	agentId?: string;
	assetId?: string;
};

export type NamedItem = {
	id: string;

	name: string;
};

export type Dependency = {
	accountId: string;
	accountName: string;
	description: string;
	id: string;
	name: string;
	parentId: string;
	dependencyType: string;
	type: string;
};

export function getMonitors(payload?: GetMonitorsPayload) {
	return new ApiRequest<PagedList<GridDataItem<MonitorsGridEntry>>>({
		url: 'monitors',
		accountBased: true,
		subaccountsFilter: true,
		method: 'POST',
		payload,
	});
}

type GetMonitorsFiltersArgs = {
	accountId?: string;
};

export function getMonitorsFilters(args?: GetMonitorsFiltersArgs) {
	return new ApiRequest<RulesConfiguration>({
		url: 'monitors/ruleConfiguration',
		accountBased: true,
		subaccountsFilter: true,
		method: 'GET',
		accountId: args?.accountId ?? ApplicationState.accountId,
	});
}

export function deleteMonitors(store: GridStore<MonitorsGridEntry>) {
	return store.getSelectionApiRequest<NamedItem[]>({
		url: 'monitors/delete',
	});
}

export function startMaintenance(
	store: GridStore<MonitorsGridEntry>,
	maintenanceData: { bulkMaintenance: Record<string, string> },
) {
	return store.getSelectionApiRequest({
		url: 'monitors/startMaintenance',
		payload: maintenanceData,
	});
}

export function endMaintenance(store: GridStore<MonitorsGridEntry>) {
	return store.getSelectionApiRequest({
		url: 'monitors/endMaintenance',
	});
}

export function resetMonitorIndex(store: GridStore<MonitorsGridEntry>) {
	return store.getSelectionApiRequest({
		url: 'monitors/resetAgentHealthIndexes',
		payload: {
			resetIndexes: {
				user: ApplicationState.userName,
				description: 'Reset agent health indexes',
			},
		},
	});
}

export function setTags(store: GridStore<MonitorsGridEntry>, tags: string[]) {
	return store.getSelectionApiRequest({
		url: 'monitors/tags',
		payload: {
			tags,
		},
	});
}

export function checkDependencies(monitorId: string) {
	return new ApiRequest<Dependency[]>({
		url: 'monitors/' + monitorId + '/dependencies',
		accountBased: true,
	});
}

export function copyMonitor<TMonitor>(accountId: string, monitorId: string, responseType: Constructor<TMonitor>) {
	return new ApiRequest<TMonitor>({
		url: `accounts/${accountId}/monitors/${monitorId}/copy`,
		responseType: responseType,
	});
}

export type GetMonitorsWithTheSameNameArgs = {
	name: string;
	type: MonitorType;
	accountId?: string;
};

export function getMonitorsWithTheSameName(args: GetMonitorsWithTheSameNameArgs) {
	const group = RuleDefinition.fieldEqualRule('name', args.name);

	const filterObj = {
		monitorType: args.type,
		filter: serialize(group),
	};

	return new ApiRequest<PagedList<MonitorsGridEntry>>({
		url: 'monitors/search/lite',
		accountBased: true,
		accountId: args.accountId,
		includeSubaccounts: false,
		method: 'POST',
		payload: filterObj,
	});
}

export function getApiDocumentation(accountId: string = null) {
	return new ApiRequest<ApiDocumentationSection[]>({
		url: 'monitors/apiHelp',
		accountId: accountId,
		accountBased: true,
		responseTypeArray: ApiDocumentationSection,
	});
}

export type MonitorTypeResult = {
	type: MonitorType;
};

export function getMonitorType(monitorId: string, accountId?: string) {
	return new ApiRequest<MonitorTypeResult>({
		url: `monitors/${monitorId}/type`,
		accountBased: true,
		accountId: accountId,
	});
}

export type LoadableMonitorStore<TMonitor extends MonitorBase<TMonitor>> = {
	id: string;
	accountId: string;
	sourceId: string;
	monitor: TMonitor;
	defaultMonitor: TMonitor;
	loaded: boolean;
	loading: boolean;

	name: string;

	errorLoadingMonitor: boolean;
};

export function getMaintenanceCalendarForMonitor(args: MonitorsGridEntry) {
	return new ApiRequest<ServiceableMonitor>({
		url: `monitors/${args.id}/maintenance`,
		accountBased: true,
		accountId: args.accountId,
	});
}

export function saveMaintenanceCalendarForMonitor(
	args: MonitorsGridEntry,
	data: { calendar: string; maintenanceCalendars: string[] },
) {
	return new ApiRequest<ServiceableMonitor>({
		url: `monitors/${args.id}/maintenance`,
		accountBased: true,
		accountId: args.accountId,
		payload: data,
	});
}

export async function loadMonitor<TMonitor extends MonitorBase<TMonitor>>(
	monitorStore: LoadableMonitorStore<TMonitor>,
	monitorConstructor: Constructor<TMonitor>,
	getExistingMonitor: (id: string) => ApiRequest<TMonitor>,
	getNewMonitor: () => ApiRequest<TMonitor>,
) {
	monitorStore.loading = true;
	const defaultMonitorResponse = await apiFetch(getNewMonitor());
	if (!defaultMonitorResponse.success) {
		monitorStore.errorLoadingMonitor = true;
		monitorStore.loading = false;
		return;
	}

	monitorStore.defaultMonitor = defaultMonitorResponse.data;
	if (monitorStore.sourceId || monitorStore.id) {
		const apiRequest = monitorStore.sourceId
			? copyMonitor<TMonitor>(monitorStore.accountId, monitorStore.sourceId, monitorConstructor)
			: getExistingMonitor(monitorStore.id);

		const response = await apiFetch(apiRequest);

		if (!response.success) {
			monitorStore.errorLoadingMonitor = true;
			monitorStore.loading = false;
			return;
		}

		monitorStore.monitor = response.data;
	} else {
		monitorStore.monitor = copyViaSerializr(monitorStore.defaultMonitor);
	}
	monitorStore.monitor = getMonitorUpdatesFromCache(monitorConstructor, monitorStore.monitor);

	if (!monitorStore.id) {
		monitorStore.monitor.id = null;
	}

	if (monitorStore.monitor.accountId == null || monitorStore.sourceId) {
		monitorStore.monitor.accountId = monitorStore.accountId ?? ApplicationState.accountId;
	}

	if (!monitorStore.id && monitorStore.sourceId && monitorStore.monitor.accountId != ApplicationState.accountId) {
		monitorStore.monitor.measuringAgent.targetId = null;
		monitorStore.monitor.measuringAgent.templateAssetId = null;

		monitorStore.monitor.measuringTarget.targetId = null;
		monitorStore.monitor.measuringTarget.templateAssetId = null;
	}

	if (monitorStore.name) {
		monitorStore.monitor.name = monitorStore.name;
	}

	if (monitorStore.monitor.description === null) {
		monitorStore.monitor.description = '';
	}

	monitorStore.monitor.enableValidation();

	monitorStore.loaded = true;
	monitorStore.loading = false;
}

export const getAgentDataSource = (sharedAgents: boolean, accountId: string = ApplicationState.accountId) => {
	return new CeeViewDataSource({
		transport: {
			read: {
				url:
					Settings.serverPath +
					'accounts/' +
					accountId +
					'/agents/lite?includeShared=' +
					sharedAgents +
					'&includeSubAgents=true',
				contentType: 'application/json; charset=utf-8',
				type: 'GET',
				dataType: 'json',
				cache: false,
			},
		},
		schema: {
			data: function (response: { name: string; id: string; shared?: boolean }[]) {
				response.forEach((x) => {
					if (x.shared) {
						x.name += ' (*)';
					}
				});
				return response;
			},
		},
		sort: { field: 'name', dir: 'asc' },
		error: ErrorHandler.kendoServerError,
	});
};

export const getAssetDataSource = (
	sharedAgents: boolean = true,
	accountId: string = ApplicationState.accountId,
	assetType: string = 'AGENT',
) => {
	return AssetDropDown.getDataSource(accountId, false, assetType == 'AGENT', undefined, sharedAgents);
};

export function getMonitorMaintenanceSupport(store: GridStore<MonitorsGridEntry>) {
	return store.getSelectionApiRequest({
		url: 'monitors/maintenanceSupport',
	});
}
